/*
 * Biotiful Life — localStorage wishlist (favorites)
 * No backend / no app. State lives in localStorage under `bio:wishlist`
 * as a JSON array of { id, handle, title }. Deduped by handle.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'bio:wishlist';
  const UPDATED_EVENT = 'wishlist:updated';

  const Wishlist = {
    read() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
      } catch (e) {
        return [];
      }
    },

    write(items) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        /* storage full or unavailable — fail silently */
      }
      document.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: { items } }));
    },

    has(handle) {
      return this.read().some((item) => item.handle === handle);
    },

    count() {
      return this.read().length;
    },

    handles() {
      return this.read().map((item) => item.handle);
    },

    toggle(entry) {
      const items = this.read();
      const index = items.findIndex((item) => item.handle === entry.handle);
      if (index > -1) {
        items.splice(index, 1);
      } else {
        items.push({ id: entry.id, handle: entry.handle, title: entry.title });
      }
      this.write(items);
      return index === -1; // true if now saved
    },

    remove(handle) {
      const items = this.read().filter((item) => item.handle !== handle);
      this.write(items);
    },

    /* Prune handles that failed to resolve (unpublished / deleted). */
    prune(missingHandles) {
      if (!missingHandles || !missingHandles.length) return;
      const items = this.read().filter((item) => missingHandles.indexOf(item.handle) === -1);
      this.write(items);
    },
  };

  // Expose for any other theme script that may need it.
  window.BioWishlist = Wishlist;

  /* ------------------------------------------------------------------ */
  /* <wishlist-button>                                                  */
  /* ------------------------------------------------------------------ */
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.label = this.querySelector('.wishlist-button__label');
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
    }

    connectedCallback() {
      if (this.button) {
        this.button.addEventListener('click', this.onClick);
      }
      document.addEventListener('wishlist:updated', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      if (this.button) {
        this.button.removeEventListener('click', this.onClick);
      }
      document.removeEventListener('wishlist:updated', this.sync);
    }

    get entry() {
      return {
        id: this.dataset.productId,
        handle: this.dataset.productHandle,
        title: this.dataset.productTitle,
      };
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      Wishlist.toggle(this.entry);
    }

    sync() {
      const active = Wishlist.has(this.dataset.productHandle);
      this.classList.toggle('is-active', active);
      if (this.button) {
        this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
        const labelText = active ? this.dataset.labelRemove : this.dataset.labelAdd;
        if (labelText) this.button.setAttribute('aria-label', labelText);
        if (this.label && labelText) this.label.textContent = labelText;
      }
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  /* ------------------------------------------------------------------ */
  /* <wishlist-count>                                                   */
  /* ------------------------------------------------------------------ */
  class WishlistCount extends HTMLElement {
    constructor() {
      super();
      this.update = this.update.bind(this);
    }

    connectedCallback() {
      document.addEventListener('wishlist:updated', this.update);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:updated', this.update);
    }

    update() {
      const count = Wishlist.count();
      this.textContent = count > 0 ? String(count) : '';
      this.hidden = count === 0;
    }
  }
  customElements.define('wishlist-count', WishlistCount);

  /* ------------------------------------------------------------------ */
  /* <wishlist-grid> — builds cards client-side from /products/x.js     */
  /* ------------------------------------------------------------------ */
  class WishlistGrid extends HTMLElement {
    constructor() {
      super();
      this.render = this.render.bind(this);
      this.list = this.querySelector('[data-wishlist-list]');
      this.empty = this.querySelector('[data-wishlist-empty]');
    }

    connectedCallback() {
      document.addEventListener('wishlist:updated', this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:updated', this.render);
    }

    setEmptyState(isEmpty) {
      if (this.empty) this.empty.hidden = !isEmpty;
      if (this.list) this.list.hidden = isEmpty;
    }

    async render() {
      const handles = Wishlist.handles();

      if (!handles.length) {
        if (this.list) this.list.innerHTML = '';
        this.setEmptyState(true);
        return;
      }

      this.setEmptyState(false);

      const missing = [];
      const products = await Promise.all(
        handles.map((handle) =>
          fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}products/${handle}.js`)
            .then((response) => (response.ok ? response.json() : Promise.reject(handle)))
            .catch(() => {
              missing.push(handle);
              return null;
            })
        )
      );

      // Prune unresolved products (deleted / unpublished). Triggers re-render.
      if (missing.length) {
        Wishlist.prune(missing);
        return;
      }

      const valid = products.filter(Boolean);
      if (!valid.length) {
        if (this.list) this.list.innerHTML = '';
        this.setEmptyState(true);
        return;
      }

      if (this.list) {
        this.list.innerHTML = valid.map((product) => this.cardMarkup(product)).join('');
      }
    }

    cardMarkup(product) {
      const url = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}products/${product.handle}`;
      const image = product.featured_image
        ? `${product.featured_image}${product.featured_image.indexOf('?') > -1 ? '&' : '?'}width=533`
        : '';
      const price = this.formatMoney(product.price);
      const title = this.escape(product.title);
      const addLabel = this.dataset.labelAdd || '';
      const removeLabel = this.dataset.labelRemove || '';

      return `
        <li class="wishlist-row">
          <a href="${url}" class="wishlist-row__media" tabindex="-1" aria-hidden="true">
            ${
              image
                ? `<img src="${image}" alt="${title}" loading="lazy" width="533" height="533">`
                : ''
            }
          </a>
          <div class="wishlist-row__info">
            <a href="${url}" class="wishlist-row__title">${title}</a>
            <span class="wishlist-row__price price">${price}</span>
          </div>
          <wishlist-button
            class="wishlist-button wishlist-button--card is-active wishlist-row__remove"
            data-product-id="${product.id}"
            data-product-handle="${this.escape(product.handle)}"
            data-product-title="${title}"
            data-label-add="${this.escape(addLabel)}"
            data-label-remove="${this.escape(removeLabel)}"
          >
            <button type="button" class="wishlist-button__btn" aria-pressed="true" aria-label="${this.escape(
              removeLabel
            )}">
              <span class="wishlist-button__icon" aria-hidden="true">${this.heartSvg()}</span>
            </button>
          </wishlist-button>
        </li>
      `;
    }

    heartSvg() {
      return '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-heart" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20.3C12 20.3 3.5 14.78 3.5 8.9C3.5 6.2 5.6 4 8.2 4C9.92 4 11.42 4.92 12 6.42C12.58 4.92 14.08 4 15.8 4C18.4 4 20.5 6.2 20.5 8.9C20.5 14.78 12 20.3 12 20.3Z"/></svg>';
    }

    /*
     * Classic Shopify formatMoney. `cents` is an integer (from /products/x.js).
     * Driven by window.bioMoneyFormat (output once from the drawer snippet via
     * shop.money_format). Handles the comma-separator variants so RON renders
     * as "151,00 lei" rather than the "$151.00" USD default.
     */
    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      const format = window.bioMoneyFormat || '${{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = typeof precision === 'number' ? precision : 2;
        thousands = typeof thousands === 'string' ? thousands : ',';
        decimal = typeof decimal === 'string' ? decimal : '.';

        if (isNaN(number) || number == null) {
          return '0';
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      let value = '';

      switch (format.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }

      return format.replace(placeholderRegex, value);
    }

    escape(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  /* ------------------------------------------------------------------ */
  /* <wishlist-drawer> — slide-out drawer mirroring CartDrawer          */
  /* ------------------------------------------------------------------ */
  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;

      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.classList.contains('active')) {
          this.close();
        }
      });

      const overlay = this.querySelector('#WishlistDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.setupHeaderTrigger();
    }

    setupHeaderTrigger() {
      let triggers = document.querySelectorAll('[data-wishlist-trigger]');
      // Backward compatibility: fall back to the legacy single id.
      if (!triggers.length) {
        const legacy = document.querySelector('#wishlist-icon-bubble');
        triggers = legacy ? [legacy] : [];
      }
      if (!triggers.length) return;

      triggers.forEach((trigger) => {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });

        trigger.addEventListener('keydown', (event) => {
          if (event.code === 'Space' || event.code === 'Enter') {
            event.preventDefault();
            this.open(trigger);
          }
        });
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;

      // Refresh the grid so the drawer always reflects current items.
      const grid = this.querySelector('wishlist-grid');
      if (grid && typeof grid.render === 'function') {
        grid.render();
      } else {
        document.dispatchEvent(new CustomEvent('wishlist:updated'));
      }

      const inner = this.querySelector('.drawer__inner');
      this.addEventListener(
        'transitionend',
        () => {
          if (typeof trapFocus === 'function') {
            trapFocus(inner || this, inner || this);
          }
        },
        { once: true }
      );

      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      if (this.activeElement) {
        this.activeElement.setAttribute('aria-expanded', 'true');
      }
      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement);
      }
      if (this.activeElement) {
        this.activeElement.setAttribute('aria-expanded', 'false');
        if (typeof this.activeElement.focus === 'function') {
          this.activeElement.focus();
        }
      }
      document.body.classList.remove('overflow-hidden');
    }
  }
  customElements.define('wishlist-drawer', WishlistDrawer);
})();
